import React, { useState, useEffect, useRef } from 'react';
import { Container, Form } from 'react-bootstrap';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { ArrowLeft, Send } from 'lucide-react';
import { sendEmail } from '../util_code/emailService';
import './messaging.css';

const Messaging = () => {
  const [user] = useAuthState(auth);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { chatId } = useParams(); // Get chatId from URL if present

  // Added from MessageChat component
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);

  // Mobile detection using window width
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      console.log('Screen width:', window.innerWidth, 'Is Mobile:', mobile);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // If there's a chatId in the URL, set it as the selected conversation
  useEffect(() => {
    if (chatId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === chatId);
      if (conversation) {
        setSelectedConversation(conversation);
        setLoadingMessages(true); // Reset loading state for the new conversation
      }
    }
  }, [chatId, conversations]);

  useEffect(() => {
    if (!user) return;

    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', user.uid),
      orderBy('lastMessageTime', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const conversationsList = [];
      
      for (const doc of snapshot.docs) {
        const conversationData = doc.data();
        const otherParticipantId = conversationData.participants.find(p => p !== user.uid);
        
        conversationsList.push({
          id: doc.id,
          ...conversationData,
          otherParticipantId
        });
      }

      setConversations(conversationsList);
      setLoading(false);

      // If there's a chatId, set the selected conversation
      if (chatId) {
        const conversation = conversationsList.find(c => c.id === chatId);
        if (conversation) {
          setSelectedConversation(conversation);
        }
      }
    });

    return () => unsubscribe();
  }, [user, chatId]);

  // Added from MessageChat - load messages for selected conversation
  useEffect(() => {
    if (!selectedConversation?.id) return;
    
    const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMessages(messagesList);
      setLoadingMessages(false);
    });

    return () => unsubscribe();
  }, [selectedConversation?.id]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const handleChatClick = (conversation) => {
    console.log('Chat clicked, isMobile:', isMobile);
    if (isMobile) {
      console.log('Navigating to mobile chat view');
      navigate(`/messages/chat/${conversation.id}`);
    } else {
      console.log('Setting selected conversation for desktop view');
      setSelectedConversation(conversation);
      // Reset message loading state for the new conversation
      setLoadingMessages(true);
      setMessages([]);
    }
  };

  const handleBackClick = () => {
    navigate('/messages');
  };

  // Added from MessageChat
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
      await addDoc(messagesRef, {
        content: newMessage,
        senderId: user.uid,
        timestamp: serverTimestamp()
      });

      const conversationRef = doc(db, 'conversations', selectedConversation.id);
      await updateDoc(conversationRef, {
        lastMessage: newMessage,
        lastMessageTime: serverTimestamp()
      });

      // Send email notification with regular template
      try {
        // Try to get recipient info from selectedConversation
        const recipientEmail = selectedConversation.recipientEmail || 'joelhieber1@gmail.com';
        const senderName = user.displayName || 'User';
        
        const emailData = {
          recipientEmail,
          subject: `Message from ${senderName} on Totly`,
          senderName,
          receiverName: selectedConversation.receiverName || 'User',
          messageText: newMessage,
          adTitle: selectedConversation.adTitle || 'Conversation',
        };
        
        await sendEmail(emailData);
        console.log("Email notification sent successfully");
      } catch (emailError) {
        console.error("Error sending email notification:", emailError);
        // Don't let email errors affect the main flow
      }

      setNewMessage('');
      
      // Optional: Only scroll to bottom when sending a new message
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  if (!user) {
    return (
      <Container className="mt-4">
        <h2>Please log in to view your messages</h2>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4">
        <h2>Loading messages...</h2>
      </Container>
    );
  }

  // Mobile View
  if (isMobile) {
    console.log('Rendering mobile view');
    // If there's a selected conversation or chatId, show the chat view
    if (selectedConversation || chatId) {
      return (
        <div className="chat-container">
          <div className="chat-header">
            <button onClick={handleBackClick} className="back-button">
              <ArrowLeft size={24} />
            </button>
            <div className="chat-title">
              {selectedConversation?.adTitle || 'Chat'}
            </div>
          </div>

          <div className="messages-area" ref={messagesAreaRef}>
            {messages.length === 0 ? (
              <div className="no-messages">
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`message-wrapper ${message.senderId === user.uid ? 'sent' : 'received'}`}
                >
                  <div className="message-bubble">
                    <div className="message-text">{message.content}</div>
                    <div className="message-time">{formatTime(message.timestamp)}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <Form onSubmit={handleSubmit} className="message-form">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="message-input"
                autoFocus={false}
              />
              <button type="submit" className="send-button" disabled={!newMessage.trim()}>
                <Send size={20} />
              </button>
            </Form>
          </div>
        </div>
      );
    }

    // Otherwise show the conversation list
    return (
      <div className="mobile-chat-container">
        <div className="mobile-header">
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '500', color: 'black' }}>Messages</h2>
        </div>
        <div className="chats-container">
          {conversations.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>💬</div>
              <div>No messages yet</div>
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation.id}
                className="chat-item"
                onClick={() => handleChatClick(conversation)}
              >
                <div className="chat-content">
                  <div className="chat-name">{conversation.adTitle || 'Untitled Conversation'}</div>
                  <div className="chat-message">{conversation.lastMessage || 'No messages yet'}</div>
                </div>
                <div className="chat-time">
                  {formatTime(conversation.lastMessageTime)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Desktop View - This is now the ONLY return for desktop
  console.log('Rendering desktop view');
  return (
    <Container fluid className="messaging-root p-0">
      <div className="row h-100 m-0">
        {/* Sidebar with conversation list - 30% width */}
        <div className="col-md-4 col-lg-3 p-0 h-100" style={{ 
          borderRight: '1px solid #e0e0e0', 
          overflow: 'auto',
          height: 'calc(100vh - 60px)' // Adjust based on your top navbar
        }}>
          <div className="conversations-header">
            Messages
          </div>
          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="p-3 text-center text-muted">No messages yet</div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''}`}
                  onClick={() => handleChatClick(conversation)}
                >
                  <div className="d-flex justify-content-between w-100">
                    <div className="conversation-title">{conversation.adTitle || 'Untitled Conversation'}</div>
                    <div className="conversation-time">{formatTime(conversation.lastMessageTime)}</div>
                  </div>
                  <div className="conversation-preview">
                    {conversation.lastMessage || 'No messages yet'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="col-md-8 col-lg-9 p-0 h-100">
          {selectedConversation ? (
            <div className="desktop-chat-wrapper">
              <div className="chat-container no-header">
                <div className="messages-area no-header" ref={messagesAreaRef}>
                  {messages.length === 0 ? (
                    <div className="no-messages">
                      No messages yet. Start the conversation!
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`message-wrapper ${message.senderId === user.uid ? 'sent' : 'received'}`}
                      >
                        <div className="message-bubble">
                          <div className="message-text">{message.content}</div>
                          <div className="message-time">{formatTime(message.timestamp)}</div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="input-container">
                  <Form onSubmit={handleSubmit} className="message-form">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="message-input"
                      autoFocus={false}
                    />
                    <button type="submit" className="send-button" disabled={!newMessage.trim()}>
                      <Send size={20} />
                    </button>
                  </Form>
                </div>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center">
              <div style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}>💬</div>
              <p className="text-muted">Select a conversation to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default Messaging;